/**
 * Discontinuity orientation sources for the plotting input form.
 *
 * The plotting form historically takes Dip Direction / Dip Amount directly
 * ("Known Orientation"). Two computed sources are added:
 *
 *  - Face logs (Algorithm_FaceLog3D_Rev.2_23Aug26): orientation from the
 *    discontinuity trace observed on two tunnel face logs.
 *  - Known trace chainages: orientation from the chainages of the trace at
 *    Right SPL / Left SPL / Centreline, the same math as the existing
 *    "Computing Discontinuity Orientation" activity (computing/Debugoutput.js).
 *
 * Both return the TRUE Dip Direction / Dip Amount (inclination already
 * corrected), which is exactly what the plotting form's dir/amt inputs expect.
 */

import { correctApparentDip } from "./inclination";
import { effectiveWallHeight } from "./section";

const toRadians = (angle) => angle * (Math.PI / 180);
const toDegree = (radian) => radian * (180 / Math.PI);

export const ORIENTATION_SOURCE_OPTIONS = [
  { value: "known", label: "Trace from Known Orientation" },
  { value: "facelogs", label: "Trace from Face Logs" },
  { value: "chainages", label: "Trace from Known Chainages" },
];

export const FACELOG_FIELD_NAMES = [
  "fch1",
  "fch2",
  "fappdip",
  "ftracedir",
  "fside1",
  "fd1",
  "fh1",
  "fside2",
  "fd2",
  "fh2",
];
export const TRACE_CHAINAGE_FIELD_NAMES = ["p2", "p4", "p5"];

/**
 * Rev.2 (23Aug26) replaced the face-log heights with any known point on the
 * trace, one per face: which side of the centreline it lies, how far off, and
 * how high above invert.
 *
 * Rev.1's TPVP — one vertical plane cutting the trace on both faces — was
 * withdrawn: a plane common to both faces is harder to find underground than
 * a point on either. The heights the
 * original Calculation.1 wants are now derived instead, by running each point
 * back along its own trace to the centreline.
 *
 * The projection is exact rather than approximate. A planar discontinuity cuts
 * the two faces — parallel planes — in parallel lines, so the apparent dip
 * measured on Face-1 is the slope of the trace on Face-2 as well, and the same
 * q carries both points home.
 */
export const FACE_POINT_FIELD_NAMES = ["fside1", "fd1", "fside2", "fd2"];

/** Rev.2: Right is positive, Left negative, and d = 0 is the centreline. */
export function offsetFromCentreline(side, d) {
  if (side === "center") return 0;
  const distance = Number(d);
  if (!isFinite(distance) || distance === 0) return 0;
  return side === "left" ? -distance : distance;
}

/** A face point named Left or Right but given no offset sits on the centreline. */
export const FACE_POINT_ON_CENTRELINE_ALERT = "The data point lies on Centreline.";

/** The trace is vertical at exactly 90 degrees, where tan is undefined. */
export const isVerticalTrace = (appDip) => Number(appDip) === 90;

/**
 * Below this, a slope is the arithmetic's own noise rather than a measurement.
 *
 * Calculation.3 and Calculation.4 branch on r or s being exactly zero, which
 * held while the heights were typed in: a level trace gave E = 0 to the bit.
 * Rev.2 derives them, and `Hp - X * q` leaves residue around 1e-16 — enough to
 * miss the r = 0 branch and evaluate atan(P / cos Angle) with Angle rounded to
 * 90, where cos is 6e-17 and a 45 degree dip comes back as 19.9.
 */
const NEAR_ZERO = 1e-12;
const snapToZero = (v) => (Math.abs(v) < NEAR_ZERO ? 0 : v);

/** What the form shows: whole degrees, with 360 written as 000. */
function rounded(Dir, Amt) {
  let dir = Math.round(Dir);
  if (dir === 360) dir = 0;
  return { dir, amt: Math.round(Amt) };
}

const hasValue = (v) => v !== undefined && v !== null && v !== "";

/**
 * A vertical trace crosses every height on the face, so a height read off it
 * says nothing about the plane and the trace has no left/right sense to give.
 * Rev.2 asks for neither in that case.
 */
export function faceLogInputsReady(dim) {
  if (!["fch1", "fch2", "fappdip"].every((k) => hasValue(dim[k]))) return false;
  if (!(Number(dim.fch2) > Number(dim.fch1))) return false;

  const vertical = isVerticalTrace(dim.fappdip);
  if (!vertical && dim.ftracedir !== "risesright" && dim.ftracedir !== "fallsright") {
    return false;
  }

  return [1, 2].every((n) => {
    const side = dim[`fside${n}`];
    if (!["left", "right", "center"].includes(side)) return false;
    if (side !== "center" && !hasValue(dim[`fd${n}`])) return false;
    return vertical || hasValue(dim[`fh${n}`]);
  });
}

export function chainageInputsReady(dim) {
  return TRACE_CHAINAGE_FIELD_NAMES.every((k) => hasValue(dim[k]));
}

/**
 * Vertical Trace Subroutine — AppDip = 90 (Rev.2).
 *
 * A vertical discontinuity meets each face in a vertical line, so the heights
 * say nothing and the two offsets fix the strike outright. The main routine
 * cannot see this at all: at AppDip = 90 the trace crosses every height, so
 * H1 and H2 carry no information and q is undefined.
 *
 * A vertical plane dips both ways with equal right; Rev.2 prints the lower of
 * the two, which always lands in 000-179.
 */
function verticalTraceOrientation(dim, D, I) {
  const X1 = offsetFromCentreline(dim.fside1, dim.fd1);
  const X2 = offsetFromCentreline(dim.fside2, dim.fd2);

  const Length = Number(dim.fch2) - Number(dim.fch1);
  const HD = Length * Math.cos(toRadians(I));
  if (!HD) return null;

  const DX = X2 - X1;
  const Delta = toDegree(Math.atan(Math.abs(DX) / HD));

  const normalise = (a) => ((a % 360) + 360) % 360;
  let Strike = D;
  if (DX > 0) Strike = D + Delta;
  else if (DX < 0) Strike = D - Delta;
  Strike = normalise(Strike);

  const Dir = Math.min(normalise(Strike + 90), normalise(Strike - 90));
  if (!isFinite(Dir)) return null;
  return { ...rounded(Dir, 90), dirExact: Dir, amtExact: 90, flat: false, vertical: true };
}

/**
 * Algorithm_FaceLog3D_Rev.2_23Aug26 — orientation from two tunnel face logs.
 *
 * The doc's D is the tunnel orientation towards increasing chainage and its I
 * is signed (positive = rising towards increasing chainage). The plotting
 * form uses positive for falling and negative for rising along Direction,
 * so the sign flips here.
 *
 * Rev.2 derives H1 and H2 rather than taking them: each face gives any known
 * point on the trace, which is run back along the trace to the centreline at
 * H = Hp - X * q. Calculation.1-4 below is unchanged.
 *
 * Returns { dir, amt, dirExact, amtExact, flat, vertical }, or null when the
 * inputs are degenerate.
 *
 * The rounded pair is what the form holds: its Dip Direction / Dip Amount
 * inputs validate as integers. The exact pair is carried alongside so the
 * renderers can plot without rounding.
 */
export function computeOrientationFromFaceLogs(dim) {
  const D = Number(dim.direction);
  const I = -(Number(dim.inclination) || 0);
  const AppDip = Number(dim.fappdip);

  if (isVerticalTrace(AppDip)) return verticalTraceOrientation(dim, D, I);

  const Ch1 = Number(dim.fch1);
  const Ch2 = Number(dim.fch2);
  const Length = Ch2 - Ch1;
  if (!Length) return null;

  const q =
    dim.ftracedir === "fallsright"
      ? -Math.tan(toRadians(AppDip))
      : Math.tan(toRadians(AppDip));

  // Rev.2 — project each known point back along its trace to the centreline,
  // the common plane the rest of the algorithm is written against.
  const X1 = offsetFromCentreline(dim.fside1, dim.fd1);
  const X2 = offsetFromCentreline(dim.fside2, dim.fd2);
  const H1 = Number(dim.fh1) - X1 * q;
  const H2 = Number(dim.fh2) - X2 * q;

  // Calculation.1
  const E = H2 - H1;
  const p = E / Length;

  // Calculation.2 — correction for tunnel inclination
  const Irad = toRadians(I);
  const A = Math.cos(Irad) - p * Math.sin(Irad);
  const B = Math.sin(Irad) + p * Math.cos(Irad);
  if (A === 0) return null;
  const r = snapToZero(B / A);
  const s = snapToZero(q / A);

  // Calculation.3 — Dip Amount
  const P = Math.abs(r);
  const Q = Math.abs(s);
  let Amt;
  let Angle = 0;
  if (P === 0) {
    Amt = toDegree(Math.atan(Q));
  } else {
    Angle = toDegree(Math.atan(Q / P));
    Amt = toDegree(Math.atan(P / Math.cos(toRadians(Angle))));
  }

  // Calculation.4 — Dip Direction
  if (r === 0 && s === 0) {
    // Horizontal plane: Amt = 0, Dir not applicable
    return { dir: 0, amt: 0, dirExact: 0, amtExact: 0, flat: true, vertical: false };
  }
  let Dir;
  if (r < 0 && s < 0) Dir = D + Angle;
  else if (r < 0 && s > 0) Dir = D - Angle;
  else if (r > 0 && s < 0) Dir = D + 180 - Angle;
  else if (r > 0 && s > 0) Dir = D + 180 + Angle;
  else if (r === 0 && s < 0) Dir = D + 90;
  else if (r === 0 && s > 0) Dir = D - 90;
  else if (s === 0 && r < 0) Dir = D;
  else Dir = D + 180; // s === 0 && r > 0

  if (Dir >= 360) Dir = Dir - 360;
  if (Dir < 0) Dir = Dir + 360;

  if (!isFinite(Dir) || !isFinite(Amt)) return null;
  return { ...rounded(Dir, Amt), dirExact: Dir, amtExact: Amt, flat: false, vertical: false };
}

/**
 * Orientation from the known chainages of the discontinuity trace at Right
 * SPL (p2), Left SPL (p4) and Centreline at Crown (p5) — the same math as
 * computing/Debugoutput.js, including the reverse inclination correction that
 * recovers the true Dir / Amt from the on-log apparent values.
 *
 * Returns { dir, amt } (rounded, dir wrapped to 0–359) or null when the
 * inputs are degenerate.
 */
export function computeOrientationFromChainages(dim) {
  const D = Number(dim.direction);
  const I = Number(dim.inclination) || 0;
  const WS = Number(dim.widthspl) || Number(dim.width);
  const HC = Number(dim.height) - effectiveWallHeight(dim);
  const P2 = Number(dim.p2);
  const P4 = Number(dim.p4);
  const P5 = Number(dim.p5);

  let TanAlpha, Pmid, Beta;
  if (P2 > P4) {
    TanAlpha = WS / (P2 - P4);
    Pmid = P4 + (P2 - P4) / 2;
    Beta = toDegree(Math.atan(HC / Math.abs(Pmid - P5)));
  } else {
    TanAlpha = WS / (P4 - P2);
    Pmid = P2 + (P4 - P2) / 2;
    Beta = toDegree(Math.atan(HC / Math.abs(P5 - Pmid)));
  }
  const Alpha = toDegree(Math.atan(TanAlpha));

  const Dopp = D > 180 ? D - 180 : D + 180;
  let ApDir = Pmid > P5 ? D : Dopp;

  // Beta and ApDir above are the *post-inclination* values (what the log
  // shows); reverse the inclination correction to get horizontal-tunnel
  // equivalents before back-computing true Dir / Amt.
  const { beta: BetaH, apDir: ApDirOrig } =
    I === 0 ? { beta: Beta, apDir: ApDir } : correctApparentDip(Beta, ApDir, D, -I);

  let Dir;
  if (P2 > P4) {
    Dir = ApDirOrig - (90 - Alpha);
  } else {
    Dir = ApDirOrig + (90 - Alpha);
  }
  if (Dir < 0) Dir = 360 + Dir;
  if (Dir > 360) Dir = Dir - 360;

  const TanAmt = Math.tan(toRadians(BetaH)) / Math.sin(toRadians(Alpha));
  const Amt = toDegree(Math.atan(TanAmt));

  if (!isFinite(Dir) || !isFinite(Amt)) return null;
  // A vertical plane dips both ways; the dip direction reported is the one
  // below 180. Amt shows as 90, so it is plotted as exactly vertical.
  if (Math.round(Amt) === 90) {
    const dirVertical = Dir % 180;
    return { ...rounded(dirVertical, 90), dirExact: dirVertical, amtExact: 90 };
  }
  return { ...rounded(Dir, Amt), dirExact: Dir, amtExact: Amt };
}

/**
 * A known trace or face chainage must lie strictly inside the log. Returns the error
 * message, or "" when the value is inside or the extent is not yet known.
 */
export function traceChainageRangeError(dim, name) {
  const value = Number(dim[name]);
  const start = Number(dim.startrd);
  const end = Number(dim.endrd);
  if (![dim.startrd, dim.endrd].every(hasValue) || !isFinite(start) || !isFinite(end)) return "";
  if (value > start && value < end) return "";
  return `Chainage must be > Start Ch. ${start} and < End Ch. ${end}`;
}

export function faceLogLocationReady(dim) {
  if (!hasValue(dim.fch1)) return false;
  if (!["left", "right", "center"].includes(dim.fside1)) return false;
  if (dim.fside1 !== "center" && !hasValue(dim.fd1)) return false;
  // A vertical trace is anchored at the invert instead of a measured height.
  return isVerticalTrace(dim.fappdip) || hasValue(dim.fh1);
}

/**
 * Derive the discontinuity data location from the face logs.
 *
 * Under Rev.2 the location comes for free: Face-1's known point already IS a
 * P6 "Any Point on Tunnel Surface/Face or Heading" — chainage fch1, side
 * fside1, offset fd1, height fh1. Rev.1 had to borrow the side and distance
 * from the TPVP because the height alone never said where across the tunnel
 * the reading was taken; Rev.2 asks for that outright.
 *
 * Face-1 is the anchor by convention: it is the lower chainage and the face
 * whose apparent dip is entered. Face-2 lies on the same plane and would give
 * the same trace.
 *
 * A vertical trace has no measured height — it crosses every height on the
 * face at the same offset — so it is anchored where it meets the invert.
 *
 * Returns the P6 fields, or null when the inputs are incomplete.
 */
export function deriveFaceLogLocation(dim) {
  if (!faceLogLocationReady(dim)) return null;
  const side = dim.fside1;
  return {
    orientation: "otherpoint",
    tunnelside: side,
    distancefromcenter: side === "center" ? 0 : Number(dim.fd1),
    verticalheight: isVerticalTrace(dim.fappdip) ? 0 : Number(dim.fh1),
    rd: Number(dim.fch1),
  };
}

/**
 * Face-2's known point, in the same P6 shape as deriveFaceLogLocation. It lies
 * on the same plane, so it is plotted as a second data location; the trace is
 * still anchored at Face-1.
 *
 * Returns the P6 fields, or null unless the trace came from face logs.
 */
export function faceLogSecondPoint(dim) {
  if ((dim.orientationsource || "known") !== "facelogs") return null;
  if (!faceLogLocationReady(dim)) return null;
  const side = dim.fside2;
  if (!["left", "right", "center"].includes(side)) return null;
  const vertical = isVerticalTrace(dim.fappdip);
  if (!vertical && !hasValue(dim.fh2)) return null;
  return {
    orientation: "otherpoint",
    tunnelside: side,
    distancefromcenter: side === "center" ? 0 : Number(dim.fd2),
    verticalheight: vertical ? 0 : Number(dim.fh2),
    rd: Number(dim.fch2),
  };
}

/**
 * Derive the discontinuity data location from the known trace chainages.
 *
 * No TPVP is involved here: p2/p4/p5 are themselves crossings of named points
 * on the tunnel surface, so the location is already explicit. The centreline
 * crossing (p5) is used as the anchor because it maps directly onto the
 * existing "Tunnel Centerline at Crown" location.
 *
 * Returns the location fields, or null when the inputs are incomplete.
 */
export function deriveChainageLocation(dim) {
  if (!chainageInputsReady(dim)) return null;
  return { orientation: "centerline", rd: Number(dim.p5) };
}

/** Data location for whichever source is selected; null for "known". */
export function deriveDataLocation(dim) {
  const source = dim.orientationsource || "known";
  if (source === "facelogs") return deriveFaceLogLocation(dim);
  if (source === "chainages") return deriveChainageLocation(dim);
  return null;
}

/** True when the source supplies the data location itself. */
export function sourceDerivesLocation(source) {
  return source === "facelogs" || source === "chainages";
}

/**
 * Apply a source chosen on the selection screen to a trace's dimension.
 *
 * - Known Orientation has no exact counterpart, so a stale dirExact/amtExact
 *   pair must not outlive the switch: the renderer prefers it wherever found.
 * - A computed source whose inputs are already filled recomputes its
 *   orientation and data location, so switching away and back never leaves
 *   the form contradicting its fields.
 *
 * Pure: returns a new dimension object.
 */
export function applySourceChoice(dimension, source) {
  const next = { ...dimension, orientationsource: source };

  if (source === "known") {
    next.dirExact = "";
    next.amtExact = "";
    return next;
  }

  const ready =
    source === "facelogs" ? faceLogInputsReady(next) : chainageInputsReady(next);
  if (ready) {
    const result =
      source === "facelogs"
        ? computeOrientationFromFaceLogs(next)
        : computeOrientationFromChainages(next);
    if (result) {
      next.dir = result.dir;
      next.amt = result.amt;
      next.dirExact = result.dirExact;
      next.amtExact = result.amtExact;
    }
  }

  const location = deriveDataLocation(next);
  if (location) Object.assign(next, location);
  return next;
}
